import { sortBy } from "lodash";
import React, { useState } from "react";
import { Route, Routes } from "react-router-dom";
import styled from "styled-components";

import {
  delayForIndexing,
  filterMatches,
  formatShortDate,
  undefinedIfEmpty,
} from "@examspark/common";
import {
  useQueryListClassroomUnitActivities,
  useMutationDeleteClassroomActivity,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  ListItem,
  RouterLink,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconRefreshLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { ParsedClassroom, ParsedTCMPCUnit } from "@examspark/types";

import ClassroomActivityEditorModal from "../../activity/ClassroomActivityEditorModal";
import PinToPlannerButton from "../PinToPlannerButton";

import ClassroomUnitActivity from "./ClassroomUnitActivity";

const StyledMetadata = styled.div`
  display: block;
  color: ${colorFromTheme("text")};
`;

const StyledExamDetailLink = styled(RouterLink)`
  font-size: 1.4rem;
`;

const ClassroomUnitActivities = (props: {
  classroom: ParsedClassroom;
  unit?: ParsedTCMPCUnit;
}) => {
  const [filterText, setFilterText] = useState("");
  const [isNewActivityModalOpen, setIsNewActivityModalOpen] = useState(false);
  const { data: activities, refetch } = useQueryListClassroomUnitActivities(
    props.classroom.workspaceId,
    props.classroom.id,
    props.unit?.id,
  );

  const deleteClassroomActivity = useMutationDeleteClassroomActivity();

  return (
    <>
      {props.classroom && props.unit && (
        <ClassroomActivityEditorModal
          classroom={props.classroom}
          unit={props.unit}
          isOpen={isNewActivityModalOpen}
          onRequestClose={() => setIsNewActivityModalOpen(false)}
          onSave={() => {
            refetch();
            setIsNewActivityModalOpen(false);
          }}
        />
      )}
      <Routes>
        <Route
          path="activities/:activityId"
          element={<ClassroomUnitActivity />}
        />
        <Route
          path="activities"
          element={
            <VSpace size="3rem">
              <VSpace>
                <Toolbar>
                  <TextInput
                    placeholder="Filter Assessments"
                    style={{ flex: "1" }}
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <Button
                    title="Settings"
                    variant="outlined"
                    onClick={() => refetch()}
                    icon={<IconRefreshLine />}
                  />
                  <Button
                    title="New Assessment"
                    color="create"
                    icon={<IconAddLine />}
                    onClick={() => setIsNewActivityModalOpen(true)}
                  >
                    Assessment
                  </Button>
                </Toolbar>
                {sortBy(activities, ["createdAt"])
                  .reverse()
                  .filter((act) =>
                    filterMatches(
                      filterText,
                      undefinedIfEmpty(act.examConfig?.resource?.name) ??
                        "Untitled activity",
                    ),
                  )
                  ?.map((act) => {
                    if (!act.examConfig) return null;
                    return (
                      <ListItem key={act.examConfig.id}>
                        <div>
                          <StyledExamDetailLink
                            to={`../activities/${act.examConfig.id}`}
                          >
                            {undefinedIfEmpty(act.examConfig?.resource?.name) ??
                              "Untitled activity"}
                          </StyledExamDetailLink>
                          <StyledMetadata>
                            {act.examConfig.createdAt &&
                              `Created ${formatShortDate(
                                new Date(act.examConfig.createdAt),
                              )}`}
                          </StyledMetadata>
                          <StyledMetadata>
                            {act.examConfig.resource?.description}
                          </StyledMetadata>
                        </div>
                        <Toolbar.Right>
                          <Button
                            title="Delete"
                            color="danger"
                            variant="plain"
                            onClick={async () => {
                              await ConfirmModal.show({
                                onResolving: async (result) => {
                                  if (!result) return;
                                  await deleteClassroomActivity.mutateAsync({
                                    activityKey: {
                                      id: act.id,
                                      sk: act.sk,
                                    },
                                  });
                                  await delayForIndexing();
                                  await refetch();
                                },
                                children: (
                                  <div style={{ padding: "1rem" }}>
                                    <CautionBanner variant="plain">
                                      Are you sure you want to delete this
                                      assessment?
                                    </CautionBanner>
                                  </div>
                                ),
                              });
                            }}
                          >
                            <IconTrashLine width="16" />
                          </Button>
                          <PinToPlannerButton resource={act.examConfig} />
                        </Toolbar.Right>
                      </ListItem>
                      // <CampfireActivityListItem
                      //   key={act.examConfig.id}
                      //   examConfig={act.examConfig}
                      //   // actionButtons={
                      //   //   <Button
                      //   //     title="Settings"
                      //   //     variant="outlined"
                      //   //     icon={<IconGearLine />}
                      //   //   />
                      //   // }
                      // />
                    );
                  })}
              </VSpace>
            </VSpace>
          }
        />
      </Routes>
    </>
  );
};

export default ClassroomUnitActivities;
